import { useQuery } from "@tanstack/react-query";
import { CalendarClock, User, MapPin } from "lucide-react";
import api from "@/lib/api";

const fmt12 = (t) => {
  if (!t) return "";
  const [h, m] = String(t).split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ampm}`;
};
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const plusDaysISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const dayLabel = (iso) => {
  const [y, mo, da] = iso.split("-").map(Number);
  const d = new Date(y, mo - 1, da);
  const t = todayISO();
  if (iso === t) return "Today";
  if (iso === plusDaysISO(1)) return "Tomorrow";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
};

export default function UpcomingAppointments() {
  const bookings = useQuery({ queryKey: ["enh-bookings"], queryFn: () => api.get("/enhancer/me/bookings").then((r) => r.data) });
  const holds = useQuery({ queryKey: ["venue-my-holds"], queryFn: () => api.get("/enhancer/me/venue/holds").then((r) => r.data) });

  const from = todayISO();
  const to = plusDaysISO(7);

  const items = [];
  (bookings.data || []).forEach((b) => {
    if (b.status === "cancelled") return;
    if (b.date >= from && b.date <= to) {
      items.push({ id: `b-${b.id}`, kind: "appointment", title: b.service_title, who: b.name, where: b.location, date: b.date, time: b.start_time, awaiting: b.agreements_status === "awaiting" });
    }
  });
  (holds.data || []).forEach((h) => {
    if (h.status === "cancelled" || h.status === "rejected") return;
    if (h.date >= from && h.date <= to) {
      items.push({ id: `h-${h.id}`, kind: "reservation", title: h.title || "Room reservation", who: h.status === "pending" ? "Pending approval" : "Your reservation", where: h.room_name, date: h.date, time: h.start_time });
    }
  });
  items.sort((a, b) => (a.date === b.date ? a.time.localeCompare(b.time) : a.date.localeCompare(b.date)));
  const top = items.slice(0, 6);

  if ((bookings.isLoading || holds.isLoading) && top.length === 0) return null;

  return (
    <div className="eyw-card p-6 mb-8" data-testid="enh-upcoming-appointments">
      <div className="flex items-center gap-2 mb-4">
        <CalendarClock size={18} className="text-[#144F75]" />
        <h2 className="font-serif text-xl text-[#1A242B]">Upcoming — next 7 days</h2>
        {top.length > 0 && <span className="text-xs text-white bg-[#144F75] rounded-full px-2 py-0.5" data-testid="enh-upcoming-count">{items.length}</span>}
      </div>
      {top.length === 0 ? (
        <p className="text-sm text-[#4A5D6B]" data-testid="enh-upcoming-empty">No appointments in the next 7 days. New client bookings and your EYW room reservations will appear here.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {top.map((it) => (
            <div key={it.id} className={`rounded-xl border px-4 py-3 ${it.kind === "reservation" ? "border-[#D98E73]/40 bg-[#D98E73]/5" : "border-[#E5E0D8] bg-white"}`} data-testid={`enh-upcoming-${it.id}`}>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[#144F75]">{dayLabel(it.date)} · {fmt12(it.time)}</div>
              <div className="text-sm font-semibold text-[#1A242B] mt-0.5 truncate">{it.title}</div>
              {it.awaiting && <span className="inline-block mt-1 text-[10px] uppercase px-2 py-0.5 rounded-full bg-[#D98E73]/15 text-[#B5794E]" data-testid={`appt-awaiting-${it.id}`}>awaiting agreements</span>}
              <div className="text-xs text-[#4A5D6B] mt-1 flex items-center gap-1 truncate"><User size={11} /> {it.who}</div>
              {it.where && <div className="text-xs text-[#8FA89B] flex items-center gap-1 truncate"><MapPin size={11} /> {it.where}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
